import { useEffect, useState, useCallback } from 'react';
import { defaultKeybindings } from '@/keybindings/defaults';
import type { KeyBinding } from '@/keybindings/types';
import { useEditorStore } from '@/store/editorStore';

interface ShortcutGroup {
  title: string;
  bindings: { label: string; shortcut: string }[];
}

function formatKey(binding: KeyBinding): string {
  const parts: string[] = [];
  if (binding.ctrl) parts.push('Ctrl');
  if (binding.shift) parts.push('Shift');
  if (binding.alt) parts.push('Alt');

  let keyLabel = binding.key;
  if (keyLabel === ' ') keyLabel = 'Space';
  if (keyLabel === 'ArrowUp') keyLabel = 'Up';
  if (keyLabel === 'ArrowDown') keyLabel = 'Down';
  if (keyLabel === 'ArrowLeft') keyLabel = 'Left';
  if (keyLabel === 'ArrowRight') keyLabel = 'Right';

  parts.push(keyLabel);
  return parts.join(' + ');
}

const commandLabels: Record<string, string> = {
  'node.addChild': 'Add child node',
  'node.addSiblingBelow': 'Add sibling below',
  'node.addSiblingAbove': 'Add sibling above',
  'node.editText': 'Edit node text',
  'node.stopEditing': 'Stop editing',
  'node.delete': 'Delete node',
  'node.moveUp': 'Move node up',
  'node.moveDown': 'Move node down',
  'node.toggleCollapse': 'Toggle collapse',
  'nav.up': 'Navigate up',
  'nav.down': 'Navigate down',
  'nav.left': 'Navigate left',
  'nav.right': 'Navigate right',
  'edit.undo': 'Undo',
  'edit.redo': 'Redo',
  'view.zoomIn': 'Zoom in',
  'view.zoomOut': 'Zoom out',
  'view.fitToScreen': 'Fit to screen',
  'search.open': 'Open search',
  'connection.startAdd': 'Start adding connection',
  'connection.cancel': 'Cancel connection',
};

const categoryOrder: { key: string; title: string; prefixes: string[] }[] = [
  { key: 'creation', title: 'Node Creation', prefixes: ['node.add'] },
  { key: 'editing', title: 'Node Editing', prefixes: ['node.edit', 'node.stop', 'node.delete', 'node.move', 'node.toggle'] },
  { key: 'navigation', title: 'Navigation', prefixes: ['nav.'] },
  { key: 'view', title: 'View', prefixes: ['view.'] },
  { key: 'edit', title: 'Edit', prefixes: ['edit.'] },
  { key: 'search', title: 'Search', prefixes: ['search.'] },
  { key: 'connections', title: 'Connections', prefixes: ['connection.'] },
];

function groupBindings(): ShortcutGroup[] {
  const groups: ShortcutGroup[] = [];

  for (const cat of categoryOrder) {
    const bindings = defaultKeybindings
      .filter((b) => cat.prefixes.some((p) => b.commandId.startsWith(p)))
      .map((b) => ({
        label: commandLabels[b.commandId] ?? b.commandId,
        shortcut: formatKey(b),
      }));
    if (bindings.length > 0) {
      groups.push({ title: cat.title, bindings });
    }
  }

  return groups;
}

export function ShortcutOverlay() {
  const [visible, setVisible] = useState(false);
  const mode = useEditorStore((s) => s.mode);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === '?' && mode === 'normal' && !visible) {
        e.preventDefault();
        setVisible(true);
        return;
      }
      if (e.key === 'Escape' && visible) {
        e.preventDefault();
        setVisible(false);
      }
    },
    [mode, visible],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (!visible) return null;

  const groups = groupBindings();

  return (
    <div
      onClick={() => setVisible(false)}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#0f172a',
          border: '1px solid #1e293b',
          borderRadius: 12,
          padding: '24px 32px',
          maxWidth: 600,
          maxHeight: '80vh',
          overflowY: 'auto',
          fontFamily: 'system-ui, -apple-system, sans-serif',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20,
          }}
        >
          <h2 style={{ color: '#f1f5f9', margin: 0, fontSize: 18, fontWeight: 600 }}>
            Keyboard Shortcuts
          </h2>
          <button
            onClick={() => setVisible(false)}
            style={{
              background: 'none',
              border: 'none',
              color: '#64748b',
              cursor: 'pointer',
              fontSize: 20,
              padding: '0 4px',
              lineHeight: 1,
            }}
          >
            x
          </button>
        </div>

        {groups.map((group) => (
          <div key={group.title} style={{ marginBottom: 16 }}>
            <h3
              style={{
                color: '#94a3b8',
                fontSize: 12,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                margin: '0 0 8px 0',
              }}
            >
              {group.title}
            </h3>
            <div
              style={{
                background: '#1e293b',
                borderRadius: 8,
                padding: '4px 0',
              }}
            >
              {group.bindings.map((b, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '6px 12px',
                  }}
                >
                  <span style={{ color: '#cbd5e1', fontSize: 13 }}>{b.label}</span>
                  <kbd
                    style={{
                      background: '#0f172a',
                      color: '#e2e8f0',
                      border: '1px solid #334155',
                      borderRadius: 4,
                      padding: '2px 8px',
                      fontSize: 12,
                      fontFamily: 'monospace',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {b.shortcut}
                  </kbd>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div style={{ color: '#475569', fontSize: 12, textAlign: 'center', marginTop: 8 }}>
          Press <kbd style={{ fontFamily: 'monospace' }}>?</kbd> to toggle this overlay
        </div>
      </div>
    </div>
  );
}
